/**
 * Full Voluum pass: totals for all phone+source rows matching filters (limit “All” only).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import type { PhoneStatsFilteredAggregateResponse } from '@/features/smswiser-campaigns/types/workspace.types';
import { useStatsTrackerParams } from '@/features/trackers/stats-tracker-context';
import { unwrapApiData } from '@/hooks/smswiser-campaign-workspace/utils';
import api from '@/services/api';

import type { FiltersState } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared';

export type UsePhoneStatsFilteredAggregateInput = {
  countryKey: string;
  wiserIds: number[];
  from: string;
  to: string;
  currency: string;
  filtersState: FiltersState;
  /** Same as server CSV “exclude cross-source noted rows” (phone tab “All”). */
  excludeCrossSourceNotedRows?: boolean;
  /** After main phones-with-stats succeeded (same range); refreshes when this timestamp changes. */
  phonesDataUpdatedAt: number | undefined;
  enabled: boolean;
};

export function cloneFiltersState(fs: FiltersState): FiltersState {
  return {
    active: fs.active,
    filters: fs.filters.map((f) => ({ ...f })),
  };
}

export function phoneStatsAggregateFiltersFingerprint(fs: FiltersState): string {
  return JSON.stringify({
    active: fs.active,
    filters: fs.filters.map((f) => ({
      field: f.field,
      operator: f.operator,
      value: f.value,
    })),
  });
}

export function usePhoneStatsFilteredAggregate(input: UsePhoneStatsFilteredAggregateInput) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const trackerParams = useStatsTrackerParams();

  return useQuery({
    queryKey: [
      'phone-stats-filtered-aggregate',
      input.countryKey,
      wiserIds.join(','),
      input.from,
      input.to,
      input.currency,
      phoneStatsAggregateFiltersFingerprint(input.filtersState),
      input.excludeCrossSourceNotedRows === true,
      input.phonesDataUpdatedAt ?? 0,
      ...Object.values(trackerParams),
    ],
    queryFn: async () => {
      const res = await api.post<{ data: PhoneStatsFilteredAggregateResponse }>(
        `smswiser/campaigns/${input.countryKey}/phones-stats-filtered-aggregate`,
        {
          wiserIds: wiserIds.join(','),
          from: input.from,
          to: input.to,
          tz: 'Europe/Paris',
          currency: input.currency,
          filters: input.filtersState.filters.map((f) => ({
            field: f.field,
            operator: f.operator,
            value: f.value,
          })),
          filtersActive: input.filtersState.active,
          excludeCrossSourceNotedRows: input.excludeCrossSourceNotedRows === true,
          ...trackerParams,
        },
        { timeout: 0 },
      );
      return unwrapApiData<PhoneStatsFilteredAggregateResponse>(res.data);
    },
    enabled:
      input.enabled &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}
